import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import StatusBadge from "../ui/StatusBadge";

const maskRatioCache = new Map();

const parseViewBoxString = (svgText) => {
  const match = svgText.match(/viewBox=["']([^"']+)["']/i);
  if (!match) return null;
  const parts = match[1].split(/[\s,]+/).map(Number);
  if (parts.length >= 4 && parts.every((value) => Number.isFinite(value))) {
    return { width: parts[2], height: parts[3] };
  }
  return null;
};

const parseSizeString = (svgText) => {
  const widthMatch = svgText.match(/width=["']([^"']+)["']/i);
  const heightMatch = svgText.match(/height=["']([^"']+)["']/i);
  if (!widthMatch || !heightMatch) return null;
  const width = Number(widthMatch[1]);
  const height = Number(heightMatch[1]);
  if (Number.isFinite(width) && Number.isFinite(height)) {
    return { width, height };
  }
  return null;
};

const getRatioFromSvg = (maskSvg) => {
  if (!maskSvg) return null;
  if (maskRatioCache.has(maskSvg)) return maskRatioCache.get(maskSvg);
  const cleaned = maskSvg.replace(/<\?xml[\s\S]*?\?>\s*/i, "").trim();
  if (!cleaned) return null;
  const wrapper = document.createElement("div");
  wrapper.style.position = "absolute";
  wrapper.style.left = "-9999px";
  wrapper.style.top = "-9999px";
  wrapper.style.visibility = "hidden";
  wrapper.style.pointerEvents = "none";
  wrapper.innerHTML = cleaned;
  const svgEl = wrapper.querySelector("svg");
  if (!svgEl) return null;
  const viewBox = parseViewBoxString(cleaned) || parseSizeString(cleaned);
  if (viewBox && viewBox.width && viewBox.height) {
    svgEl.setAttribute("viewBox", `0 0 ${viewBox.width} ${viewBox.height}`);
    svgEl.setAttribute("width", String(viewBox.width));
    svgEl.setAttribute("height", String(viewBox.height));
  }
  document.body.appendChild(wrapper);
  const shapes = svgEl.querySelectorAll("path,rect,circle,ellipse,polygon");
  if (!shapes.length) {
    document.body.removeChild(wrapper);
    return null;
  }
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  shapes.forEach((shape) => {
    const box = shape.getBBox();
    minX = Math.min(minX, box.x);
    minY = Math.min(minY, box.y);
    maxX = Math.max(maxX, box.x + box.width);
    maxY = Math.max(maxY, box.y + box.height);
  });
  document.body.removeChild(wrapper);
  const width = maxX - minX;
  const height = maxY - minY;
  if (!(width > 0 && height > 0)) return null;
  const ratio = width / height;
  maskRatioCache.set(maskSvg, ratio);
  return ratio;
};

export function ItemPeek({ item, className, onClose, onMore, onAddToBasket, isItemInBasket }) {
  const rootRef = useRef(null);
  const objectRef = useRef(null);
  
  const baseRatio = useMemo(() => {
    if (!item?.maskSvg) return null;
    const cleaned = item.maskSvg.replace(/<\?xml[\s\S]*?\?>\s*/i, "").trim();
    if (!cleaned) return null;
    const viewBox = parseViewBoxString(cleaned) || parseSizeString(cleaned);
    if (!viewBox || !viewBox.width || !viewBox.height) return null;
    return viewBox.width / viewBox.height;
  }, [item?.maskSvg]);
  
  const [maskRatio, setMaskRatio] = useState(baseRatio);
  
  useLayoutEffect(() => {
    setMaskRatio(baseRatio || null);
  }, [baseRatio]);
  
  useLayoutEffect(() => {
    if (!item?.maskSvg) return;
    const ratio = getRatioFromSvg(item.maskSvg);
    if (ratio) setMaskRatio(ratio);
  }, [item?.maskSvg]);
  
  useLayoutEffect(() => {
    const root = rootRef.current;
    const objectEl = objectRef.current;
    if (!root || !objectEl || !item) return;
    const updateLayout = () => {
      const rootWidth = root.offsetWidth || root.getBoundingClientRect().width;
      const ratio = maskRatio && Number.isFinite(maskRatio) ? maskRatio : 1;
      const ratioClamped = Math.min(Math.max(ratio, 0.7), 1.6);
      const rawHeight = (rootWidth / ratioClamped) * 0.8;
      const maxHeight = Math.min(320, rootWidth);
      const objectHeight = Math.min(maxHeight, Math.max(120, rawHeight));
      root.style.setProperty("--peek-object-height", `${objectHeight}px`);
      const rootHeight =
        root.offsetHeight || root.getBoundingClientRect().height;
      const objectTop = objectEl.offsetTop;
      const objectHeightMeasured = objectEl.offsetHeight;
      const rootCenter = rootHeight / 2;
      const objectCenter = objectTop + objectHeightMeasured / 2;
      const offset = rootCenter - objectCenter;
      root.style.setProperty("--peek-offset", `${offset}px`);
    };
    updateLayout();
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(updateLayout);
    observer.observe(root);
    return () => observer.disconnect();
  }, [item, maskRatio]);

  // Early return AFTER all hooks
  if (!item) return null;

  const handleClose = (event) => {
    event.preventDefault();
    event.stopPropagation();
    onClose?.();
  };
  const handleMore = (event) => {
    event.preventDefault();
    event.stopPropagation();
    onMore?.();
  };
  const handleAddToBasket = (event) => {
    event.preventDefault();
    event.stopPropagation();
    onAddToBasket?.(item);
  };
  const handleRootClick = (event) => {
    if (event.target.closest(".g-item-peek__btn")) return;
    event.stopPropagation();
    onClose?.();
  };
  const headerDetails = [
    item.productName ? `Name: ${item.productName}` : "",
    item.warehouse ? `Author: ${item.warehouse}` : "",
  ].filter(Boolean);
  const deriveColorSummary = () => {
    if (Array.isArray(item.colors) && item.colors.length) {
      return item.colors.join(", ");
    }
    if (item.colors) return item.colors;
    if (item.dominantColor) return item.dominantColor;
    return null;
  };

  const colorSummary = deriveColorSummary();

  const footerDetails = [
    item.usage ? `Use: ${item.usage}` : "",
    item.size ? `Size: ${item.size}` : "",
    colorSummary ? `Color: ${colorSummary}` : "",
  ].filter(Boolean);
  const peekStyle = maskRatio ? { "--mask-ratio": maskRatio } : undefined;
  const statusForBadge = (item.status || "available").toLowerCase();
  return (
    <div
      className={`g-item-peek${className ? ` ${className}` : ""}`}
      role="dialog"
      aria-label={`${item.title || "Item"} details`}
      onClick={handleRootClick}
      style={peekStyle}
      ref={rootRef}
    >
      <div className="g-item-peek__body">
        <div className="g-item-peek__title">{item.title}</div>
        {headerDetails.length ? (
          <div className="g-item-peek__meta">
            {headerDetails.map((detail) => (
              <div key={detail} className="g-item-peek__meta-row">
                {detail}
              </div>
            ))}
          </div>
        ) : null}
      </div>
      <div
        className="g-item-peek__object"
        aria-hidden="true"
        ref={objectRef}
      ></div>
      <div className="g-item-peek__footer">
        {footerDetails.length ? (
          <div className="g-item-peek__meta">
            {footerDetails.map((detail) => (
              <div key={detail} className="g-item-peek__meta-row">
                {detail}
              </div>
            ))}
          </div>
        ) : null}
        <div className="g-item-peek__chip">
          <StatusBadge status={statusForBadge} />
        </div>
        <div className="g-item-peek__actions">
          <button
            className="g-item-peek__btn"
            type="button"
            onClick={handleMore}
          >
            More details
          </button>
          <button 
            className={`g-item-peek__btn ${isItemInBasket?.(item.id) ? 'g-item-peek__btn--remove' : ''}`}
            type="button"
            onClick={handleAddToBasket}
          >
            {isItemInBasket?.(item.id) ? 'Remove from basket' : 'Add to basket'}
          </button>
          <button
            className="g-item-peek__btn g-item-peek__btn--ghost"
            type="button"
            onClick={handleClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
